import { httpsCallable } from "firebase/functions";

import { functions } from "../firebase";

/*
 * members / memberPhones / storeMemberships are locked to
 * `allow read, write: if false` in firestore.rules on purpose (see
 * functions/index.js for the full explanation) — nothing here talks to
 * Firestore directly anymore. Every customer/points operation goes
 * through a Cloud Function instead, gated by requireActiveStore() on
 * the server rather than by security rules.
 */

const findOrRegisterCustomerCallable = httpsCallable(
  functions,
  "storeFindOrRegisterCustomer"
);

const getMemberCallable = httpsCallable(functions, "storeGetMember");

const getMemberByBarcodeCallable = httpsCallable(
  functions,
  "storeGetMemberByBarcode"
);

const listMemberBalancesCallable = httpsCallable(
  functions,
  "storeListMemberBalances"
);

const awardPointsCallable = httpsCallable(functions, "storeAwardPoints");

const redeemPointsCallable = httpsCallable(functions, "storeRedeemPoints");

/**
 * Cloud Function errors arrive as a FirebaseError with .code like
 * "functions/failed-precondition" and .message set to whatever the
 * function threw. Custom error codes (e.g. ACCOUNT_SUSPENDED) travel in
 * .details, since HttpsError's third argument becomes error.details on
 * the client. This normalizes both into the shape the UI already
 * expects (error.message, error.code, error.memberId).
 */
function toClientError(error) {
  const message =
    error?.message || "Something went wrong. Please try again.";

  const clientError = new Error(message);
  const details = error?.details || {};

  clientError.code = details.code || error?.code;
  clientError.memberId = details.memberId;

  return clientError;
}

/**
 * Looks up a customer by phone number. If the number is new, a "Guest"
 * member is registered automatically (the customer sets their real name
 * later via the customer app). If the customer exists but isn't a member
 * of this store yet, they're joined automatically. Returns
 * { memberId, wasNewMember, wasAddedToStore, member, membership }.
 */
export async function findOrRegisterCustomer(phoneNumber) {
  try {
    const response = await findOrRegisterCustomerCallable({ phoneNumber });
    return response.data;
  } catch (error) {
    throw toClientError(error);
  }
}

/**
 * Loads an already-known member (e.g. by the memberId in the URL) plus
 * this store's own membership record.
 */
export async function getMemberAtStore(memberId) {
  try {
    const response = await getMemberCallable({ memberId });
    return response.data;
  } catch (error) {
    throw toClientError(error);
  }
}

/**
 * Looks up a customer by their scanned PointsHub barcode ("PH:<memberId>"
 * or bare digits). Joins the member to this store on the fly if they
 * haven't visited it before, same as findOrRegisterCustomer. Returns
 * { member, membership }.
 */
export async function getMemberByBarcode(barcodeValue) {
  try {
    const response = await getMemberByBarcodeCallable({ barcodeValue });
    return response.data;
  } catch (error) {
    throw toClientError(error);
  }
}

/**
 * Lists every store where this member has a positive points balance, so
 * staff can pick a source store when redeeming cross-store.
 */
export async function listMemberStoreBalances(memberId) {
  try {
    const response = await listMemberBalancesCallable({ memberId });
    return response.data.balances;
  } catch (error) {
    throw toClientError(error);
  }
}

/** Awards points for a purchase at the signed-in employee's store. */
export async function awardPoints({
  memberId,
  totalAmount,
  eligibleAmount,
  lookupMethod = "phone",
}) {
  try {
    const response = await awardPointsCallable({
      memberId,
      totalAmount,
      eligibleAmount,
      lookupMethod,
    });

    return response.data;
  } catch (error) {
    throw toClientError(error);
  }
}

/**
 * Redeems points from a customer's balance at sourceStoreId for use at
 * the signed-in employee's store. Same-store redemption pays full value;
 * redeeming from a different store pays crossStoreRedemptionPercent of
 * the value (a 30% reduction by default). Redemption is only reachable
 * after scanning the customer's PointsHub code, so lookupMethod is
 * always "scan" — the server rejects any other value.
 */
export async function redeemPoints({
  memberId,
  sourceStoreId,
  pointsToRedeem,
}) {
  try {
    const response = await redeemPointsCallable({
      memberId,
      sourceStoreId,
      pointsToRedeem,
      lookupMethod: "scan",
    });

    return response.data;
  } catch (error) {
    throw toClientError(error);
  }
}
